/**
 * Interaction Detail page (spec §6, Task 6): the drill-down from every list
 * row (My Calls, Call History, Queues) into one call's journey, quality and
 * evidence. Split into `InteractionDetail` (route + fetch) and exported
 * `InteractionDetailBody` (pure props) so the presentational half can be
 * unit-tested under SSR without a router or network.
 */
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Accordion,
  AccordionHeader,
  AccordionItem,
  AccordionPanel,
  Badge,
  Button,
  Card,
  CardHeader,
  Text,
  makeStyles,
  tokens,
} from '@fluentui/react-components';
import { ArrowLeftFilled, AlertRegular } from '@fluentui/react-icons';
import { useAuth } from '../auth/TeamsAuthProvider';
import { useTimezone } from '../common/TimezoneProvider';
import { PageHeader } from '../common/PageHeader';
import { ViewAsAction } from '../common/ViewAsAction';
import { LoadingState } from '../common/LoadingState';
import { ErrorState } from '../common/ErrorState';
import { KpiTile } from '../common/KpiTile';
import { CallEvidenceCards } from './CallEvidenceCards';
import { JourneyFlow } from './JourneyFlow';
import { ReportProblemDialog } from './ReportProblemDialog';
import { api } from '../../services/api';
import type { CallDetail, CallLegsResponse, SupportReportSummary, UserInfo } from '../../types';
import { collapseLegsForRole, tierForUser } from '../../utils/journeyTiers';
import { journeyNarrative } from '../../utils/journeyNarrative';
import { formatPartyLabel, formatPhoneNumber } from '../../utils/phone';
import { deriveOutcome, outcomeTone, outcomeInputForDetail, qualityWord } from '../../utils/interactionOutcome';
import { formatCallDuration } from '../../utils/callDisplay';
import { strings } from '../../strings/en';

const useStyles = makeStyles({
  content: {
    padding: tokens.spacingHorizontalL,
    maxWidth: '1200px',
    margin: '0 auto',
    display: 'flex',
    flexDirection: 'column',
    rowGap: tokens.spacingVerticalL,
  },
  backRow: {
    display: 'flex',
    alignItems: 'center',
  },
  topBlock: {
    display: 'flex',
    flexDirection: 'column',
    rowGap: tokens.spacingVerticalM,
  },
  tileRow: {
    display: 'grid',
    // 190px keeps a formatted date-time value ("Sep 15, 1:54 PM") on one
    // line at desktop widths (owner send-back F5a).
    gridTemplateColumns: 'repeat(auto-fit, minmax(190px, 1fr))',
    gap: tokens.spacingHorizontalM,
  },
  actions: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalM,
  },
  cardBody: {
    padding: tokens.spacingHorizontalM,
    display: 'flex',
    flexDirection: 'column',
    rowGap: tokens.spacingVerticalXS,
  },
});

export interface InteractionDetailBodyProps {
  call: CallDetail;
  legs: CallLegsResponse | null;
  user: UserInfo | null;
  report: SupportReportSummary | null;
  onReport: () => void;
  formatDateTime?: (iso: string | null) => string;
}

export function InteractionDetailBody({
  call,
  legs,
  user,
  report,
  onReport,
  formatDateTime = (v) => v ?? '',
}: InteractionDetailBodyProps) {
  const styles = useStyles();
  // G3 (owner morning review): a raw caller-ID number must never reach the
  // screen unformatted, and a caller-ID name must be shown together with
  // its number rather than replacing it.
  const party = call.direction === 'outbound'
    ? formatPhoneNumber(call.called_number)
    : formatPartyLabel(call.calling_name, call.calling_number);
  const outcome = deriveOutcome(outcomeInputForDetail(call, legs));
  const tier = tierForUser(user);
  const journey = legs
    ? collapseLegsForRole(legs, {
        tier,
        direction: call.direction,
        ownedNumbers: user?.owned_numbers ?? [],
        ingressSigGroup: call.ingress_sig_group,
        // Fallback caller-ID for a genuine single-leg Teams-only call, where
        // the Teams call session's own caller/callee metadata for the
        // external PSTN party can be sparse -- without this, the journey
        // showed a bare "?" node instead of the known caller-ID (bug fix).
        detail: {
          callingNumber: call.calling_number,
          callingName: call.calling_name,
          calledNumber: call.called_number,
          outcome: call.outcome,
          durationSeconds: call.duration_seconds,
        },
      })
    : null;
  const narrative = journey ? journeyNarrative(journey) : strings.interaction.journeyUnavailable;
  const status = legs?.voiceiq_assessment?.overall_status ?? 'unknown';
  const reason = legs?.voiceiq_assessment?.overall_reason || strings.quality.unknownReason;
  const canSeeTechnical = user?.can_see_technical_details === true;

  return (
    <div className={styles.content}>
      <div className={styles.topBlock}>
        <Text size={600} weight="semibold">
          {strings.interaction.with(party ?? '?')}
        </Text>
        <div className={styles.tileRow}>
          <KpiTile size="tile" label={strings.callHistory.outcome} value={outcome} valueTone={outcomeTone(outcome)} />
          <KpiTile
            size="tile"
            label={strings.interaction.direction}
            value={call.direction === 'outbound' ? strings.interaction.directionOutbound : strings.interaction.directionInbound}
          />
          <KpiTile
            size="tile"
            label={strings.interaction.started}
            value={formatDateTime(call.reporting_time)}
            noWrapValue
          />
          <KpiTile size="tile" label={strings.myCalls.tiles.talkTime} value={formatCallDuration(call.duration_seconds)} />
        </div>
        <div className={styles.actions}>
          {report ? (
            <Badge appearance="tint" color="brand">
              {strings.interaction.reportedRef(report.reference)}
            </Badge>
          ) : null}
          <Button appearance="primary" icon={<AlertRegular />} onClick={onReport}>
            {strings.interaction.reportProblem}
          </Button>
        </div>
      </div>

      <Card appearance="filled">
        <CardHeader header={<Text weight="semibold">{strings.interaction.journeyCard}</Text>} description={strings.interaction.journeyHint} />
        <div className={styles.cardBody}>
          {journey ? <JourneyFlow journey={journey} narrative={narrative} /> : <Text>{narrative}</Text>}
        </div>
      </Card>

      <Card appearance="filled">
        <CardHeader header={<Text weight="semibold">{strings.interaction.qualityCard}</Text>} />
        <div className={styles.cardBody}>
          <Text size={500} weight="semibold">
            {qualityWord(status)}
          </Text>
          <Text size={200}>{reason}</Text>
        </div>
      </Card>

      <Accordion collapsible defaultOpenItems={canSeeTechnical ? ['evidence'] : []}>
        <AccordionItem value="evidence">
          <AccordionHeader>{strings.interaction.evidence}</AccordionHeader>
          <AccordionPanel>
            <CallEvidenceCards call={call} canSeeTechnical={canSeeTechnical} locationLabel={user?.location_label_singular ?? 'Branch'} />
          </AccordionPanel>
        </AccordionItem>
      </Accordion>
    </div>
  );
}

interface InteractionDetailProps {
  emulationPanelOpen: boolean;
  onToggleEmulationPanel: () => void;
}

export function InteractionDetail({ emulationPanelOpen, onToggleEmulationPanel }: InteractionDetailProps) {
  const styles = useStyles();
  const { sessionId } = useParams<{ sessionId: string }>();
  const { user, isAuthenticated } = useAuth();
  const { formatDateTime } = useTimezone();
  const navigate = useNavigate();
  const [call, setCall] = useState<CallDetail | null>(null);
  const [legs, setLegs] = useState<CallLegsResponse | null>(null);
  const [report, setReport] = useState<SupportReportSummary | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [reportDialogOpen, setReportDialogOpen] = useState(false);

  useEffect(() => {
    if (!isAuthenticated || !sessionId) return;
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        setError(null);
        const c = await api.getCallDetail(sessionId);
        if (cancelled) return;
        setCall(c);
        const [l, r] = await Promise.all([
          c.multi_session_id ? api.getCallLegs(c.multi_session_id).catch(() => null) : Promise.resolve(null),
          api.getSupportReportForInteraction(sessionId).catch(() => null),
        ]);
        if (cancelled) return;
        setLegs(l);
        setReport(r);
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : 'Failed to load');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isAuthenticated, sessionId]);

  const header = (
    <PageHeader
      title={strings.interaction.title}
      breadcrumb={strings.interaction.breadcrumb}
      actions={<ViewAsAction emulationPanelOpen={emulationPanelOpen} onToggleEmulationPanel={onToggleEmulationPanel} />}
    />
  );

  if (loading && !call) {
    return (
      <div>
        {header}
        <LoadingState variant="page" />
      </div>
    );
  }
  if (error || !call) {
    return (
      <div>
        {header}
        <div className={styles.content}>
          <ErrorState message={error} onRetry={() => navigate(0)} />
        </div>
      </div>
    );
  }
  return (
    <div>
      {header}
      <div className={styles.content}>
        <div className={styles.backRow}>
          <Button appearance="subtle" icon={<ArrowLeftFilled />} onClick={() => navigate(-1)}>
            {strings.interaction.back}
          </Button>
        </div>
      </div>
      <InteractionDetailBody
        call={call}
        legs={legs}
        user={user}
        report={report}
        onReport={() => setReportDialogOpen(true)}
        formatDateTime={formatDateTime}
      />
      {sessionId ? (
        <ReportProblemDialog
          open={reportDialogOpen}
          interactionId={sessionId}
          onClose={() => setReportDialogOpen(false)}
          onSubmitted={(r) => {
            setReport(r);
            if (!r.is_duplicate) setReportDialogOpen(false);
          }}
        />
      ) : null}
    </div>
  );
}
